import { Box } from '@mui/material'
import { useMemo, useRef, useState } from 'react'
import {
  WorkCentreParametersDataType,
  WorkCentreParametersTableProps,
} from './Tables.types'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { format } from 'date-fns'

export const WorkCentreParametersTable = ({
  data,
  isLoading,
  updateTableData,
  updateParametersData,
  isUploading,
}: WorkCentreParametersTableProps) => {
  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, WorkCentreParametersDataType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Base source site code',
        field: 'baseSourceSiteCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Work centre code',
        field: 'workCentreCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Batch grouping code',
        field: 'batchGroupingCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'SKU code',
        field: 'skuCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Unit of measure conversion to batch',
        field: 'UnitOfMeasureConversionToBatch',
        flex: 1,
        type: 'number', // Assuming this is a numeric field
      },
      {
        headerName: 'Production lead time',
        field: 'productionLeadTime',
        flex: 1,
        type: 'number', // Assuming this is a numeric field
        editable: true,
      },
      {
        headerName: 'Run rate',
        field: 'runRate',
        flex: 1,
        type: 'number', // Assuming this is a numeric field
        editable: true,
      },
      {
        headerName: 'Minimum production order quantity',
        field: 'minimumProductionOrderQuantity',
        flex: 1,
        type: 'number', // Assuming this is a numeric field
        editable: true,
      },
      {
        headerName: 'Minimum production order increment',
        field: 'minimumProductionOrderIncrement',
        flex: 1,
        type: 'number', // Assuming this is a numeric field
        editable: true,
      },
      {
        headerName: 'Is primary work centre',
        field: 'isPrimaryWorkCentre',
        flex: 1,
        type: 'boolean',
      },
      {
        headerName: 'Valid from date',
        field: 'validFromDate',
        flex: 1,
        type: 'string', // Assuming this is a date field stored as string
        valueFormatter: (params) => {
          if (!params) return '' // Handle missing dates
          return format(new Date(params), 'yyyy-MM-dd')
        },
      },
      {
        headerName: 'Valid to date',
        field: 'validToDate',
        flex: 1,
        type: 'string', // Assuming this is a date field stored as string
        valueFormatter: (params) => {
          if (!params) return '' // Handle missing dates
          return format(new Date(params), 'yyyy-MM-dd')
        },
      },
    ],
    [data]
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={updateParametersData} // Pass upload function to BaseTable
        isUploading={isUploading}
        isSaving={false}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="WorkCentreParameters"
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
        }}
      />
    </Box>
  )
}
